import { Component, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService, IOpenIDClaims } from 'core-client/api';

// Error types for categorization
export enum AuthErrorType {
	NETWORK = 'NETWORK',
	PROVIDER = 'PROVIDER',
	CONFIG = 'CONFIG',
	TOKEN = 'TOKEN',
	USER = 'USER',
	TIMEOUT = 'TIMEOUT',
	UNKNOWN = 'UNKNOWN'
}

// Error details interface
export interface AuthErrorDetails {
	title: string;
	message: string;
}

@Component({
	selector: 'app-auth-callback',
	imports: [
		SharedModule
	],
	templateUrl: './auth-callback.component.html',
	styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
	private readonly destroy$: Subject<void> = new Subject<void>();
	private authCheckTimeout?: number;
	private readonly AUTH_TIMEOUT_MS: number = 30000;
	private readonly SCHEDULED_REDIRECT_TIMEOUT: number = 5000;

	// UI state
	hasError: boolean = false;
	loadingMessage: string = 'PAGE_IS_LOADING';

	// Error display properties
	errorType: AuthErrorType = AuthErrorType.UNKNOWN;
	errorDetails: AuthErrorDetails | null = null;

	// Network status
	isOnline: boolean = true;
	isCheckingConnection: boolean = false;

	constructor(
		private readonly authService: AuthService,
		private readonly router: Router
	) { }

	ngOnInit(): void {
		this.setupNetworkListener();
		this.setupConnectionCheckListener();
		this.monitorAuthState();
		this.monitorConfigErrors();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.authCheckTimeout) {
			clearTimeout(this.authCheckTimeout);
		}
	}

	/**
	 * Monitor authentication state changes
	 */
	private monitorAuthState(): void {
		this.authService.isAuthenticated$()
			.pipe(takeUntil(this.destroy$))
			.subscribe(isAuthenticated => {
				if (isAuthenticated && !this.hasError) {
					this.processOAuthCallback()
				} else {
					// Setup timeout for stuck authentication
					this.setupAuthTimeout();
				}
			});
	}

	/**
	 * Monitor OAuth configuration errors with provider unavailability detection
	 */
	private monitorConfigErrors(): void {
		this.authService.oauthConfigError$()
			.pipe(takeUntil(this.destroy$))
			.subscribe(hasConfigError => {
				if (hasConfigError && !this.hasError) {
					// Check if provider is unavailable
					if (!this.authService.isProviderAvailable()) {
						this.setError(
							AuthErrorType.PROVIDER,
							'PROVIDER_UNAVAILABLE',
							'PROVIDER_UNAVAILABLE_MESSAGE'
						);

						// Auto-redirect to login after showing error
						this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT);  // 5 second delay
					} else {
						this.setError(
							AuthErrorType.CONFIG,
							'OAUTH_CONFIG_ERROR',
							'OAUTH_CONFIG_ERROR_MESSAGE'
						);

						// Auto-redirect to login after showing error
						this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT);
					}
				}
			});
	}

	private scheduleLoginRedirect(delayMs: number): void {
		console.log(`[AuthCallback] Redirecting to login in ${delayMs / 1000}s...`);

		setTimeout(() => {
			console.log('[AuthCallback] Redirecting to login page');
			this.navigateToLogin();
		}, delayMs);
	}

	/**
	 * Monitor connection checking state
	 */
	private setupConnectionCheckListener(): void {
		this.authService.isCheckingConnection$()
			.pipe(takeUntil(this.destroy$))
			.subscribe(isChecking => {
				this.isCheckingConnection = isChecking;

				if (isChecking) {
					this.loadingMessage = 'CHECKING_CONNECTION';
				} else if (this.authService.isAuthenticated()) {
					this.loadingMessage = 'LOGIN_CHECK';
				}
			});
	}

	/**
	 * Process OAuth callback
	 */
	private processOAuthCallback(): void {
		try {
			this.loadingMessage = 'LOGIN_CHECK';

			// Check if already authenticated
			if (this.authService.isAuthenticated()) {
				queueMicrotask(async () => {
					await this.handleSuccessfulAuth();
				});
				return;
			}

			// Setup timeout for stuck authentication
			this.setupAuthTimeout();

		} catch (error) {
			console.error('[AuthCallback] Error during callback processing:', error);
			this.handleCallbackError(error);
		}
	}

	/**
	 * Setup authentication timeout
	 */
	private setupAuthTimeout(): void {
		this.authCheckTimeout = globalThis.window.setTimeout(() => {
			if (!this.hasError && !this.authService.isAuthenticated()) {
				console.warn('[AuthCallback] Authentication timeout');
				this.setError(
					AuthErrorType.TIMEOUT,
					'LOGIN_TIMEOUT',
					'LOGIN_TIMEOUT_MESSAGE'
				);

				// Auto-redirect after timeout
				this.scheduleLoginRedirect(this.SCHEDULED_REDIRECT_TIMEOUT);
			}
		}, this.AUTH_TIMEOUT_MS);
	}

	/**
	 * Handle successful authentication
	 */
	private async handleSuccessfulAuth(): Promise<void> {
		if (this.authCheckTimeout) {
			clearTimeout(this.authCheckTimeout);
		}

		this.loadingMessage = 'LOGIN_CHECK';

		try {
			const state = history.state;
			if (state?.oAuthUser) {
				const oAuthUser = state.oAuthUser as { info: IOpenIDClaims; };
				await this.authService.handleAuthenticationSuccess(oAuthUser);
				if (this.authService.userEnabled) {
					// User is enabled, navigate to dashboard
					await this.router.navigate(['/dashboard']);
				} else {
					// User is disabled
					this.setError(
						AuthErrorType.USER,
						'ACCOUNT_DISABLED',
						'ACCOUNT_DISABLED_MESSAGE'
					);
				}
			} else {
				// No user data in state
				console.warn('[AuthCallback] No oAuthUser in state');
				this.setError(
					AuthErrorType.TOKEN,
					'USER_DATA_MISSING',
					'USER_DATA_MISSING_MESSAGE'
				);
			}
		} catch (error) {
			console.error('[AuthCallback] Error during authentication:', error);
			this.setError(
				AuthErrorType.UNKNOWN,
				'USER_DATA_ERROR',
				'USER_DATA_ERROR_MESSAGE'
			);
		}
	}

	/**
	 * Handle callback error based on error type
	 */
	private handleCallbackError(error: any): void {
		console.error('[AuthCallback] Callback error:', error);

		// Check provider availability first
		if (!this.authService.isProviderAvailable()) {
			this.setError(
				AuthErrorType.PROVIDER,
				'PROVIDER_UNAVAILABLE',
				'PROVIDER_UNAVAILABLE_MESSAGE'
			);
			return;
		}

		// Categorize by error characteristics
		if (error?.message?.includes('timeout')) {
			this.setError(
				AuthErrorType.TIMEOUT,
				'LOGIN_TIMEOUT',
				'LOGIN_TIMEOUT_MESSAGE'
			);
		} else if (error?.status === 0) {
			// Network error (provider check passed, so likely network issue)
			this.setError(
				AuthErrorType.NETWORK,
				'NETWORK_ERROR',
				'NETWORK_ERROR_MESSAGE'
			);
		} else if (error?.status === 401 || error?.status === 403) {
			this.setError(
				AuthErrorType.TOKEN,
				'TOKEN_ERROR',
				'TOKEN_ERROR_MESSAGE'
			);
		} else {
			this.setError(
				AuthErrorType.UNKNOWN,
				'LOGIN_PROCESSING_ERROR',
				'UNEXPECTED_ERROR_MESSAGE'
			);
		}
	}

	/**
	 * Consolidated error setter - single source of truth
	 */
	private setError(type: AuthErrorType, titleKey: string, messageKey: string): void {
		this.hasError = true;
		this.errorType = type;
		this.errorDetails = {
			title: titleKey,
			message: messageKey
		};
	}

	/**
	 * Setup network status listener
	 */
	private setupNetworkListener(): void {
		this.authService.isOnline$()
			.pipe(takeUntil(this.destroy$))
			.subscribe((isOnline: boolean) => {
				this.isOnline = isOnline;

				// If network goes down and not showing error yet
				if (!isOnline && !this.hasError) {
					this.setError(
						AuthErrorType.NETWORK,
						'NETWORK_ERROR',
						'NETWORK_ERROR_MESSAGE'
					);
				}
			});
	}

	/**
	 * Navigate back to login page
	 */
	navigateToLogin(): void {
		this.router.navigate(['']);
	}
}
